/**
 * LeafBlockRenderer — Gamma-quality leaf block rendering.
 * Rich visual treatment for all 15 leaf block types with proper
 * typography, spacing, image handling, and inline editing.
 */
import { useCallback, useRef } from "react";
import type { BlockType } from "../../../data/types";

interface LeafBlockRendererProps {
  blockType: BlockType;
  content: any;
  readOnly: boolean;
  onUpdate: (content: any) => void;
  /** Resolve relative asset paths (e.g. assets/hero.png) to full URLs */
  resolveAsset?: (src: string) => string;
  /** Dark mode — inverts text colors */
  dark?: boolean;
}

/** Inline editable text component */
function InlineText({
  value,
  onChange,
  readOnly,
  className,
  placeholder,
  tag: Tag = "div",
}: {
  value: string;
  onChange: (val: string) => void;
  readOnly: boolean;
  className?: string;
  placeholder?: string;
  tag?: "div" | "span" | "p";
}) {
  const ref = useRef<HTMLElement>(null);

  const handleBlur = useCallback(() => {
    const text = ref.current?.innerText ?? "";
    if (text !== value) onChange(text);
  }, [value, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      (ref.current as HTMLElement)?.blur();
    }
  }, []);

  return (
    <Tag
      ref={ref as any}
      contentEditable={!readOnly}
      suppressContentEditableWarning
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`outline-none ${
        readOnly ? "" : "hover:ring-1 hover:ring-indigo-200/50 focus:ring-2 focus:ring-indigo-300/60 rounded-lg px-1 -mx-1"
      } ${className ?? ""}`}
      data-placeholder={placeholder}
    >
      {value || ""}
    </Tag>
  );
}

export default function LeafBlockRenderer({
  blockType,
  content,
  readOnly,
  onUpdate,
  resolveAsset,
  dark,
}: LeafBlockRendererProps) {
  const textBase = dark ? "text-white/90" : "text-slate-800";
  const textMuted = dark ? "text-white/60" : "text-slate-500";
  const textSub = dark ? "text-white/70" : "text-slate-600";

  const resolveSrc = (src: string) => {
    if (!src) return "";
    if (resolveAsset) return resolveAsset(src);
    return src;
  };

  switch (blockType) {
    case "h1":
      return (
        <div className="pb-3">
          <InlineText
            value={typeof content === "string" ? content : ""}
            onChange={onUpdate}
            readOnly={readOnly}
            className={`text-[3.25rem] leading-[1.1] font-extrabold tracking-tight ${textBase}`}
            placeholder="Heading 1"
          />
        </div>
      );

    case "h2":
      return (
        <div className="pt-4 pb-2">
          <InlineText
            value={typeof content === "string" ? content : ""}
            onChange={onUpdate}
            readOnly={readOnly}
            className={`text-[1.75rem] leading-[1.25] font-bold tracking-tight ${textBase}`}
            placeholder="Heading 2"
          />
        </div>
      );

    case "h3":
      return (
        <div className="pt-3 pb-1.5">
          <InlineText
            value={typeof content === "string" ? content : ""}
            onChange={onUpdate}
            readOnly={readOnly}
            className={`text-[1.25rem] leading-[1.35] font-semibold ${textBase}`}
            placeholder="Heading 3"
          />
        </div>
      );

    case "text":
      return (
        <div className="py-1.5">
          <InlineText
            value={typeof content === "string" ? content : ""}
            onChange={onUpdate}
            readOnly={readOnly}
            className={`text-[1.05rem] leading-[1.75] ${textSub}`}
            placeholder="Type something..."
          />
        </div>
      );

    case "bullets": {
      const items: string[] = typeof content === "object" && Array.isArray(content?.items) ? content.items : [];
      return (
        <div className="py-2">
          <ul className="space-y-2.5 pl-1">
            {items.map((item: string, i: number) => (
              <li key={i} className="flex items-start gap-3">
                <span className={`mt-[0.55rem] size-1.5 rounded-full shrink-0 ${dark ? "bg-indigo-400/70" : "bg-indigo-400"}`} />
                <span className={`text-[1.05rem] leading-[1.7] ${textSub}`}>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    case "quote":
      return (
        <div className="py-3 my-2">
          <blockquote className={`pl-5 border-l-[3px] ${dark ? "border-white/30" : "border-indigo-400"}`}>
            <InlineText
              value={typeof content === "string" ? content : ""}
              onChange={onUpdate}
              readOnly={readOnly}
              className={`text-[1.1rem] leading-[1.7] italic ${dark ? "text-white/80" : "text-slate-600"}`}
              placeholder="Quote text..."
            />
          </blockquote>
        </div>
      );

    case "callout": {
      const calloutType = typeof content === "object" ? content?.type ?? "info" : "info";
      const calloutText = typeof content === "object" ? content?.text ?? "" : String(content);
      const styles: Record<string, { bg: string; border: string; text: string; icon: string }> = {
        info: { bg: "bg-blue-50/80", border: "border-blue-200/60", text: "text-blue-900", icon: "ℹ️" },
        warning: { bg: "bg-amber-50/80", border: "border-amber-200/60", text: "text-amber-900", icon: "⚠️" },
        error: { bg: "bg-red-50/80", border: "border-red-200/60", text: "text-red-900", icon: "🚨" },
        success: { bg: "bg-emerald-50/80", border: "border-emerald-200/60", text: "text-emerald-900", icon: "✅" },
      };
      const s = styles[calloutType] ?? styles.info;
      return (
        <div className={`py-2 my-2 px-5 py-4 rounded-xl border ${s.bg} ${s.border} backdrop-blur-sm`}>
          <div className="flex items-start gap-3">
            <span className="text-lg mt-0.5 shrink-0">{s.icon}</span>
            <InlineText
              value={calloutText}
              onChange={(text) => onUpdate({ type: calloutType, text })}
              readOnly={readOnly}
              className={`text-[0.95rem] leading-[1.65] ${s.text}`}
              placeholder="Callout text..."
            />
          </div>
        </div>
      );
    }

    case "image": {
      const src = typeof content === "object" ? content?.src : content;
      const alt = typeof content === "object" ? content?.alt ?? "" : "";
      const caption = typeof content === "object" ? content?.caption : undefined;
      const resolvedSrc = resolveSrc(src || "");
      return (
        <div className="py-4 my-1">
          {resolvedSrc ? (
            <figure>
              <div className="rounded-2xl overflow-hidden shadow-lg shadow-black/8 ring-1 ring-black/5">
                <img
                  src={resolvedSrc}
                  alt={alt}
                  className="w-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                    const parent = (e.target as HTMLImageElement).parentElement;
                    if (parent) {
                      parent.innerHTML = `<div class="flex items-center justify-center h-48 bg-gradient-to-br from-slate-100 to-slate-50 text-sm text-slate-400">Image not available</div>`;
                    }
                  }}
                />
              </div>
              {caption && (
                <figcaption className={`mt-2.5 text-center text-[0.85rem] ${textMuted}`}>
                  {caption}
                </figcaption>
              )}
            </figure>
          ) : (
            <div className={`flex items-center justify-center h-48 rounded-2xl border-2 border-dashed ${
              dark ? "border-white/20 bg-white/5" : "border-slate-200 bg-slate-50"
            }`}>
              <div className="text-center">
                <div className="text-3xl mb-2 opacity-40">🖼️</div>
                <span className={`text-[0.85rem] ${textMuted}`}>Image placeholder</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    case "code": {
      const language = typeof content === "object" ? content?.language ?? "" : "";
      const source = typeof content === "object" ? content?.source ?? "" : String(content);
      return (
        <div className="py-3 my-1">
          <div className="rounded-xl overflow-hidden shadow-md shadow-black/5 ring-1 ring-black/5">
            {language && (
              <div className="flex items-center px-4 py-2 bg-slate-800 border-b border-slate-700/50">
                <span className="text-[0.7rem] font-mono font-medium text-slate-400 uppercase tracking-wider">
                  {language}
                </span>
              </div>
            )}
            <pre className="p-5 bg-[#0d1117] text-[0.85rem] font-mono leading-relaxed overflow-x-auto">
              <code className="text-emerald-300/90">{source}</code>
            </pre>
          </div>
        </div>
      );
    }

    case "divider":
      return (
        <div className="py-6 flex items-center justify-center">
          <div className={`flex items-center gap-3 w-full max-w-xs`}>
            <div className={`flex-1 h-px ${dark ? "bg-white/15" : "bg-slate-200"}`} />
            <div className={`size-1.5 rounded-full ${dark ? "bg-white/20" : "bg-slate-300"}`} />
            <div className={`flex-1 h-px ${dark ? "bg-white/15" : "bg-slate-200"}`} />
          </div>
        </div>
      );

    case "spacer": {
      const height = typeof content === "object" ? content?.height ?? 32 : 32;
      return <div style={{ height }} />;
    }

    case "video": {
      const src = typeof content === "object" ? content?.src : content;
      const poster = typeof content === "object" ? content?.poster : undefined;
      const alt = typeof content === "object" ? content?.alt ?? "" : "";
      const resolvedSrc = resolveSrc(src || "");
      const resolvedPoster = poster ? resolveSrc(poster) : undefined;
      return (
        <div className="py-3 my-1">
          {resolvedSrc ? (
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-black/8 ring-1 ring-black/5">
              {/* preload="metadata" so the page render doesn't pull
                  the entire file just because a slide has a video on
                  it. The browser fetches duration + first frame and
                  waits for an actual play. */}
              <video
                src={resolvedSrc}
                poster={resolvedPoster}
                aria-label={alt}
                controls
                preload="metadata"
                className="w-full"
              />
            </div>
          ) : (
            <div className={`flex items-center justify-center h-48 rounded-2xl border-2 border-dashed ${
              dark ? "border-white/20 bg-white/5" : "border-slate-200 bg-slate-50"
            }`}>
              <div className="text-center">
                <div className="text-3xl mb-2 opacity-40">🎬</div>
                <span className={`text-[0.85rem] ${textMuted}`}>Video placeholder</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    case "embed": {
      const src = typeof content === "object" ? content?.src : content;
      return (
        <div className="py-3 my-1">
          {src ? (
            <div className="rounded-2xl overflow-hidden shadow-lg shadow-black/8 ring-1 ring-black/5">
              <iframe src={src} className="w-full h-80" title="Embed" />
            </div>
          ) : (
            <div className={`flex items-center justify-center h-48 rounded-2xl border-2 border-dashed ${
              dark ? "border-white/20 bg-white/5" : "border-slate-200 bg-slate-50"
            }`}>
              <div className="text-center">
                <div className="text-3xl mb-2 opacity-40">🔗</div>
                <span className={`text-[0.85rem] ${textMuted}`}>Embed placeholder</span>
              </div>
            </div>
          )}
        </div>
      );
    }

    case "mermaid": {
      const source = typeof content === "string" ? content : "";
      return (
        <div className="py-3 my-1">
          <div className={`rounded-xl overflow-hidden ${dark ? "bg-white/5 ring-1 ring-white/10" : "bg-slate-50 ring-1 ring-slate-200/60"} p-5`}>
            <div className={`text-[0.7rem] font-mono uppercase tracking-wider mb-3 ${textMuted}`}>
              mermaid diagram
            </div>
            <pre className={`text-[0.85rem] font-mono leading-relaxed whitespace-pre-wrap ${dark ? "text-white/70" : "text-slate-600"}`}>
              {source}
            </pre>
          </div>
        </div>
      );
    }

    case "toggle": {
      const title = typeof content === "object" ? content?.title ?? "Toggle" : "Toggle";
      const toggleContent = typeof content === "object" ? content?.content ?? "" : String(content);
      return (
        <details className="py-2 my-1 group">
          <summary className={`cursor-pointer text-[1.05rem] font-semibold ${textBase} hover:text-indigo-600 transition-colors flex items-center gap-2`}>
            <span className="text-indigo-400 group-open:rotate-90 transition-transform text-sm">▶</span>
            {title}
          </summary>
          <div className={`mt-3 pl-6 text-[1rem] leading-[1.7] ${textSub}`}>
            {toggleContent}
          </div>
        </details>
      );
    }

    case "table": {
      const headers = typeof content === "object" ? content?.headers ?? [] : [];
      const tableRows = typeof content === "object" ? content?.rows ?? [] : [];
      return (
        <div className="py-3 my-1 overflow-x-auto">
          <div className="rounded-xl overflow-hidden ring-1 ring-black/5 shadow-sm">
            <table className="w-full text-[0.9rem]">
              {headers.length > 0 && (
                <thead>
                  <tr className={dark ? "bg-white/10" : "bg-slate-50"}>
                    {headers.map((h: string, i: number) => (
                      <th key={i} className={`px-5 py-3 text-left font-semibold ${textBase} border-b ${dark ? "border-white/10" : "border-slate-200"}`}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {tableRows.map((row: string[], ri: number) => (
                  <tr key={ri} className={ri % 2 === 0 ? "" : (dark ? "bg-white/5" : "bg-slate-50/50")}>
                    {row.map((cell: string, ci: number) => (
                      <td key={ci} className={`px-5 py-3 ${textSub} border-b ${dark ? "border-white/5" : "border-slate-100"}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    default:
      return (
        <div className={`py-2 px-4 text-[0.85rem] ${textMuted} rounded-xl ${dark ? "bg-white/5" : "bg-slate-50"} ring-1 ${dark ? "ring-white/10" : "ring-slate-200"}`}>
          {blockType}: {typeof content === "string" ? content : JSON.stringify(content)}
        </div>
      );
  }
}
